import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import styled from '@emotion/styled'

import EditIcon from '@mui/icons-material/Edit'

import axios from '../../dataClient/dataClient'
import { allSettingsSections } from '../../routes/settingsRoutes'
import { FlexColumn, FlexEndContainer, OutlineContainer, SpaceBetweenRowContainer, VerticallyCenteredFlexColumn } from '../styles/Containers'
import Button, { SecondaryButton } from '../common/Button'
import Loading from '../common/Loading'
import LoadingError from '../common/LoadingError'
import Breadcrumbs from '../settings/Breadcrumbs'
import Letter from '../letters/Letter'
import DeleteLetterButton from '../letters/DeleteLetterButton'
import EditableLetterTemplate from '../letterTemplates/EditableLetterTemplate'
import LetterTemplateTable from '../letterTemplates/LetterTemplateTable'

const HeaderRow = styled(SpaceBetweenRowContainer)`
  min-height: 37px;
  align-items: center;
`

function formatLetterTemplate(template) {
  return { ...template, updatedAt: new Date(template.updatedAt) }
}

// Deferred so we don't read allSettingsSections during the settingsRoutes ↔ this-page circular import.
function getBaseCrumbs() {
  const { templates } = allSettingsSections
  return [
    { label: templates.label, path: templates.path },
    { label: templates.subsections.letters.label, path: templates.subsections.letters.path }
  ]
}

function SettingsTemplateLettersPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [listLoading, setListLoading] = useState(true)
  const [listError, setListError] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState(false)
  const [letterTemplates, setLetterTemplates] = useState([])
  const [activeTemplate, setActiveTemplate] = useState()

  const baseCrumbs = getBaseCrumbs()
  const letterTemplateId = searchParams.get('letterTemplateId')
  const isEditing = Boolean(letterTemplateId) && searchParams.get('edit') === 'true'
  const isCreating = searchParams.get('create') === 'true'
  const isListView = !isCreating && !letterTemplateId
  const isDetailView = !isCreating && !isEditing && Boolean(letterTemplateId) && Boolean(activeTemplate)

  function getLetterTemplates() {
    setListLoading(true)
    setListError(false)
    axios
      .get('/letter/letter-template')
      .then(({ data }) => setLetterTemplates(data.data.map(formatLetterTemplate)))
      .catch(e => {
        console.error('Failed to retrieve letter templates', e)
        setListError(true)
      })
      .finally(() => setListLoading(false))
  }

  function getActiveLetterTemplate(id) {
    setDetailLoading(true)
    setDetailError(false)
    axios
      .get(`/letter/letter-template/${id}`)
      .then(({ data }) => setActiveTemplate(formatLetterTemplate(data.data)))
      .catch(e => {
        console.error('Failed to retrieve letter template', id, e)
        setDetailError(true)
      })
      .finally(() => setDetailLoading(false))
  }

  const handleSelectTemplate = id => setSearchParams({ letterTemplateId: id })
  const handleEditTemplate = id => setSearchParams({ letterTemplateId: id, edit: 'true' })
  const handleCreateNewTemplate = () => setSearchParams({ create: 'true' })

  function handleCancelUpdateTemplate() {
    if (isEditing) setSearchParams({ letterTemplateId })
    else setSearchParams({})
  }

  function handleUpdateTemplate(template) {
    setActiveTemplate(formatLetterTemplate(template))
    getLetterTemplates()
    setSearchParams({ letterTemplateId: template.id })
  }

  function handleDeleteTemplate() {
    getLetterTemplates()
    setSearchParams({})
  }

  useEffect(() => {
    getLetterTemplates()
  }, [])

  // The active template lives in the url so the breadcrumbs & browser history can navigate back to the list.
  useEffect(() => {
    if (!letterTemplateId) {
      setActiveTemplate()
      setDetailLoading(false)
      setDetailError(false)
      return
    }
    setActiveTemplate(prev => (prev?.id === letterTemplateId ? prev : undefined))
    getActiveLetterTemplate(letterTemplateId)
  }, [letterTemplateId])

  let crumbs = baseCrumbs
  if (isCreating) crumbs = [...baseCrumbs, { label: 'New Template' }]
  else if (isEditing) crumbs = [...baseCrumbs, { label: 'Edit Template' }]
  else if (activeTemplate) crumbs = [...baseCrumbs, { label: activeTemplate.name }]

  if ((isListView && listLoading) || (letterTemplateId && !isCreating && detailLoading)) {
    const loadingText = isListView && listLoading ? 'templates' : 'template'
    return (
      <FlexColumn height="100%" width="100%" gap="10px">
        <HeaderRow>
          <Breadcrumbs crumbs={baseCrumbs} />
        </HeaderRow>
        <Loading>Loading letter {loadingText}...</Loading>
      </FlexColumn>
    )
  }

  if ((isListView && listError) || (letterTemplateId && !isCreating && detailError)) {
    const errorText = isListView && listError ? 'templates' : 'template'
    return (
      <FlexColumn width="100%" gap="10px">
        <HeaderRow>
          <Breadcrumbs crumbs={baseCrumbs} />
        </HeaderRow>
        <VerticallyCenteredFlexColumn height="100%">
          <LoadingError message={[`There was an error loading the letter ${errorText}. Please refresh the page to try again.`]} />
        </VerticallyCenteredFlexColumn>
      </FlexColumn>
    )
  }

  return (
    <FlexColumn width="100%" gap="10px">
      {(isListView || isDetailView) && (
        <HeaderRow gap="10px">
          <Breadcrumbs crumbs={crumbs} />
          {isListView && <Button onClick={handleCreateNewTemplate}>Create New Template</Button>}
          {isDetailView && (
            <FlexEndContainer gap="5px">
              <DeleteLetterButton state="TEMPLATE" id={activeTemplate.id} name={activeTemplate.name} onSuccess={handleDeleteTemplate} />
              <SecondaryButton onClick={() => handleEditTemplate(activeTemplate.id)}>
                <EditIcon /> Edit Template
              </SecondaryButton>
            </FlexEndContainer>
          )}
        </HeaderRow>
      )}
      {isCreating && (
        <EditableLetterTemplate isCreating handleUpdateTemplate={handleUpdateTemplate} handleCancelUpdateTemplate={handleCancelUpdateTemplate}>
          <Breadcrumbs crumbs={crumbs} />
        </EditableLetterTemplate>
      )}
      {isEditing && activeTemplate && (
        <EditableLetterTemplate
          letterTemplate={activeTemplate}
          handleUpdateTemplate={handleUpdateTemplate}
          handleCancelUpdateTemplate={handleCancelUpdateTemplate}>
          <Breadcrumbs crumbs={crumbs} />
        </EditableLetterTemplate>
      )}
      {isDetailView && (
        <OutlineContainer padding="25px 30px">
          <Letter state="TEMPLATE" letter={activeTemplate} />
        </OutlineContainer>
      )}
      {isListView && (
        <OutlineContainer padding="15px 10px">
          <LetterTemplateTable
            letterTemplates={letterTemplates}
            onEditLetterTemplate={handleEditTemplate}
            handleSelectTemplate={handleSelectTemplate}
          />
        </OutlineContainer>
      )}
    </FlexColumn>
  )
}

export default SettingsTemplateLettersPage
